/** The Shows page's Media view: every link across shows (GET /api/media), filtered here. */
import { getJson } from "./request";

export interface MediaLink {
  id: number;
  show_id: number;
  number: number;
  date: string;
  venue: string;
  event: string | null;
  headliner: string | null;
  /** The band the link is of; null covers the whole show. */
  band: string | null;
  url: string;
  media_type: string;
  media_format: string;
  title: string | null;
  notes: string | null;
}
export const fetchMedia = async () => (await getJson<{ items: MediaLink[] }>("/api/media", "Could not load media")).items;

export type MediaScope = "full_set" | "clip" | "song";
export const SCOPES: { value: MediaScope; label: string }[] = [
  { value: "full_set", label: "Full sets" },
  { value: "clip", label: "Clips" },
  { value: "song", label: "Songs" },
];
export interface MediaFilters {
  q: string;
  year: number | null;
  scope: MediaScope | null;
  audio: boolean;
}

/** Every word of the search must appear in the link's band, headliner, venue, event, title or notes. */
export function filterMedia(items: MediaLink[], f: MediaFilters): MediaLink[] {
  const words = f.q.toLowerCase().split(/\s+/).filter(Boolean);
  return items.filter((m) => {
    if (f.scope && m.media_type !== f.scope) return false;
    if (f.audio && m.media_format !== "audio") return false;
    if (f.year && Number(m.date.slice(0, 4)) !== f.year) return false;
    const hay = [m.band, m.headliner, m.venue, m.event, m.title, m.notes].join(" ").toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

export interface MediaShow {
  show_id: number;
  date: string;
  venue: string;
  event: string | null;
  headliner: string | null;
  links: MediaLink[];
}
/** Consecutive links of one show, in the order sent (newest show first, then the show sheet's order). */
export function byShow(items: MediaLink[]): MediaShow[] {
  const out: MediaShow[] = [];
  for (const m of items) {
    const last = out[out.length - 1];
    if (last && last.show_id === m.show_id) last.links.push(m);
    else out.push({ show_id: m.show_id, date: m.date, venue: m.venue, event: m.event, headliner: m.headliner, links: [m] });
  }
  return out;
}

/**
 * A YouTube video's id, for its thumbnail; null for any other link. Prod's
 * links are all watch?v= (215 of 226, 2026-09-11; 3 are playlists); youtu.be
 * is what a phone's share sheet pastes.
 */
export function youtubeId(url: string): string | null {
  let u: URL;
  try {
    u = new URL(url.trim());
  } catch {
    return null;
  }
  const host = u.hostname.replace(/^www\./, "");
  if (host === "youtu.be") return /^\/([\w-]{6,})/.exec(u.pathname)?.[1] ?? null;
  return host === "youtube.com" ? u.searchParams.get("v") : null;
}
export const thumbUrl = (url: string) => {
  const id = youtubeId(url);
  return id ? `https://i.ytimg.com/vi/${id}/mqdefault.jpg` : null;
};
export function hostOf(url: string): string {
  try {
    return new URL(url.trim()).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}
